import { useApolloClient } from "@apollo/client";
import { useSidebarStore } from "@shared/ui/SidebarNavigation";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

const LOGGED_IN_USER_KEY = "logged_in_user";
const LOCALE_KEY = "locale";

const clearCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

    // Set expiration to a past date
    document.cookie =
      name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  }
};

export interface UseLogoutOptions {
  redirectUrl?: string;
}

export const useLogout = (options?: UseLogoutOptions) => {
  const navigate = useNavigate();
  const setExpanded = useSidebarStore((s) => s.setExpanded);
  const client = useApolloClient();
  const { update: updateIntercom } = useIntercom();

  const logout = () => {
    // Preserve user-specific settings (column configs, filters, locale)
    const preservedSettings: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith("user-columns:") ||
          key.endsWith("_FILTERS") ||
          key === LOCALE_KEY)
      ) {
        preservedSettings[key] = localStorage.getItem(key) || "";
      }
    }

    // Get current user ID before clearing
    const currentUserId = localStorage.getItem(LOGGED_IN_USER_KEY) || "";

    // Mixpanel cleanup
    const mixpanelToken = import.meta.env.VITE_APP_MIXPANEL_TOKEN;
    if (mixpanelToken && import.meta.env.MODE !== "development") {
      mixpanel.unregister("userEmail");
      mixpanel.unregister("userName");
      mixpanel.unregister("oemName");
      mixpanel.unregister("userRole");
      mixpanel.reset();
    }

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Restore preserved settings
    if (currentUserId) {
      localStorage.setItem(LOGGED_IN_USER_KEY, currentUserId);
    }
    Object.entries(preservedSettings).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    // Intercom cleanup
    updateIntercom({ hideDefaultLauncher: false });

    // Reset sidebar state
    setExpanded(false);

    // Clear Apollo cache and redirect
    client.clearStore().then(() => {
      // Clear cookies last, after all Apollo operations are done
      clearCookies();
      navigate(options?.redirectUrl ?? "/oem");
    });
  };

  return { logout };
};
